import React, {Props, useEffect, useState} from "react";
import ButtonRounded from "../../../components/ButtonRounded";
import Card from "@material-ui/core/Card";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import {useHistory, useLocation} from "react-router-dom";
import {useOverviewContainerStyles} from "../../../common/styles/mandateOverviewContainerStyles";
import {AdvancedSubMandate, GeneralMandate, MandateSurplusLocked, MandateSurplusAvailable, VolumeCheck} from "./preliminarySurplusCalculations";
import { calculateMandateData, calculateVolumeCheck } from "./preliminarySurplusCalculations";
import parseAndFormat from "../../../components/texts/parseAndFormat";
import { brandColors } from "../../../theme";

const useStyles = useOverviewContainerStyles;

export interface DataType {
  advancedSubMandate: AdvancedSubMandate;
  generalMandate: GeneralMandate;
  mandateSurplusLocked: MandateSurplusLocked;
  mandateSurplusAvailable: MandateSurplusAvailable;
  volumeCheck: VolumeCheck;
}

const PrelSurplusRoaduseCalculation: React.FC<Props<any>> = () => {
    const classes = useStyles();
    const history = useHistory();
    const [data, setData] = useState<DataType>({
      advancedSubMandate: {},
      generalMandate: {},
      mandateSurplusLocked: {},
      mandateSurplusAvailable: {},
      volumeCheck: {},
    });
    const location = useLocation<{
        generalMandateReqVol?: any;
        advancedMandateReqVol?: any;
        dieselVol?: any;
        dieselBioAboveMandate?: any;
        gasolineVol?: any;
        gasolineBioAboveMandate?: any;
        inJointFulfSum?: any;
        inJointFulfConventionalSum?: any;
        outJointFulfSum?: any;
        outJointFulfConventionalSum?: any;
    }>();
    const state = location.state || {};
    let { generalMandateReqVol, advancedMandateReqVol, dieselVol, dieselBioAboveMandate, gasolineVol,
    gasolineBioAboveMandate, inJointFulfSum, inJointFulfConventionalSum,
        outJointFulfSum, outJointFulfConventionalSum } = state;

    generalMandateReqVol = Math.ceil(generalMandateReqVol);
    advancedMandateReqVol = Math.ceil(advancedMandateReqVol);

    const calculateAndSetData = () => {
        // @ts-ignore
        const mandateData = calculateMandateData({
            generalMandateReqVol,
            advancedMandateReqVol,
            dieselVol,
            dieselBioAboveMandate,
            gasolineVol,
            gasolineBioAboveMandate,
            inJointFulfSum,
            inJointFulfConventionalSum,
            outJointFulfSum,
            outJointFulfConventionalSum
        });

        const volumeCheckData = calculateVolumeCheck({
            inJointFulfSum,
            inJointFulfConventionalSum,
            outJointFulfSum,
            outJointFulfConventionalSum,
            jointFulfOutAdvancedBio: mandateData.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel,
            jointFulfOutConventionalBio: mandateData.mandateSurplusLocked.joint_fulfillment_out_conventional_biofuel,
            fameSoldVol: parseFloat(dieselVol.fame),
            fameAdvSoldVol: parseFloat(dieselVol.fame_advanced),
            hvoSoldVol: parseFloat(dieselVol.hvo_hro),
            hvoAdvSoldVol: parseFloat(dieselVol.hvo_hro_advanced),
            etanolSoldVol: parseFloat(gasolineVol.etanol),
            etanolAdvSoldVol: parseFloat(gasolineVol.etanol_advanced),
            naftaSoldVol: parseFloat(gasolineVol.nafta),
            naftaAdvSoldVol: parseFloat(gasolineVol.nafta_advanced),
            biogasolineSoldVol: parseFloat(gasolineVol.bio_gasoline),
            biogasolineAdvSoldVol: parseFloat(gasolineVol.bio_gasoline_advanced),
            allocatedInAdvancedSubMandate: mandateData.generalMandate.allocated_in_advanced_sub_mandate,
            allocationAdvBioInGasolineGeneral: mandateData.generalMandate.allocation_advanced_biofuel_in_gasoline,
            allocationAdvBioInDieselGeneral: mandateData.generalMandate.allocation_advanced_biofuel_in_diesel,
            allocationConventionalBioInGasolineGeneral: mandateData.generalMandate.allocation_conventional_biofuel_in_gasoline,
            allocationConventionalBioInDieselGeneral: mandateData.generalMandate.allocation_conventional_biofuel_in_diesel,
            advancedBioInGasolineRequiredByCustomers: mandateData.mandateSurplusLocked.advanced_biofuel_in_gasoline_required_by_customers,
            advancedBioInDieselRequiredByCustomers: mandateData.mandateSurplusLocked.advanced_biofuel_in_diesel_required_by_customers,
            conventionalBioInGasolineRequiredByCustomers: mandateData.mandateSurplusLocked.conventional_biofuel_in_gasoline_required_by_customers,
            conventionalBioInDieselRequiredByCustomers: mandateData.mandateSurplusLocked.conventional_biofuel_in_diesel_required_by_customers,
            advancedBioInGasolineAvailable: mandateData.mandateSurplusAvailable.advanced_biofuel_in_gasoline,
            advancedBioInDieselAvailable: mandateData.mandateSurplusAvailable.advanced_biofuel_in_diesel,
            conventionalBioInGasolineAvailable: mandateData.mandateSurplusAvailable.conventional_biofuel_in_gasoline,
            conventionalBioInDieselAvailable: mandateData.mandateSurplusAvailable.conventional_biofuel_in_diesel
        });

        setData({
            ...mandateData,
            ...volumeCheckData
        });

    };

    const getCellColor = (cellValue: any) => {
        if (!cellValue) {
            return "#a1aeb7";
        } else if (cellValue == "N/A" || cellValue == 0 || cellValue == "") {
            return "#a1aeb7";
        } else {
            return "#424b5a";
        }
    };

    const formatDisplayValue = (value: any) => {
        if (value && value === "N/A") {
            return value;
        } else {
            return parseAndFormat(value || 0, 0);
        }
    };

    useEffect(() => {
        calculateAndSetData();
    }, [generalMandateReqVol, advancedMandateReqVol, dieselVol, dieselBioAboveMandate,
        gasolineVol, gasolineBioAboveMandate, inJointFulfSum, inJointFulfConventionalSum,
        outJointFulfSum, outJointFulfConventionalSum]);

    return (
        <div>
            <div className={classes.container}>
                <div>
                    <h1>Preliminary surplus calculation <span style={{ color: brandColors.primary.main }}>ROAD USE</span></h1>
                </div>
                <div className={classes.buttonContainer}>
                    <ButtonRounded className={classes.buttonMargin}
                                   fullWidth
                                   variant="contained"
                                   color="primary"
                                   onClick={() => history.goBack()}
                    >
                        Back
                    </ButtonRounded>
                </div>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Advanced sub-mandate</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell>Calculated mandate requirement</TableCell>
                                <TableCell>{formatDisplayValue(data.advancedSubMandate.calculated_mandate_requirement)}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.advancedSubMandate.joint_fulfillment_in_advanced_biofuel)}}>Joint fulfillment IN advanced biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.advancedSubMandate.joint_fulfillment_in_advanced_biofuel)}}>{formatDisplayValue(data.advancedSubMandate.joint_fulfillment_in_advanced_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.advancedSubMandate.joint_fulfillment_in_advanced_biofuel == "N/A" ? "Not applicable" : ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.advancedSubMandate.allocation_advanced_biofuel_in_gasoline)}}>Allocation advanced biofuel in gasoline</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.advancedSubMandate.allocation_advanced_biofuel_in_gasoline)}}>{formatDisplayValue(data.advancedSubMandate.allocation_advanced_biofuel_in_gasoline)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.advancedSubMandate.allocation_advanced_biofuel_in_diesel)}}>Allocation advanced biofuel in diesel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.advancedSubMandate.allocation_advanced_biofuel_in_diesel)}}>{formatDisplayValue(data.advancedSubMandate.allocation_advanced_biofuel_in_diesel)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Remaining volume advanced sub-mandate</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.advancedSubMandate.remaining_volume_advanced_sub_mandate)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>General mandate</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell>Calculated mandate requirement</TableCell>
                                <TableCell>{formatDisplayValue(data.generalMandate.calculated_mandate_requirement)}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.joint_fulfillment_in_advanced_biofuel)}}>Joint fulfillment IN advanced biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.joint_fulfillment_in_advanced_biofuel)}}>{formatDisplayValue(data.generalMandate.joint_fulfillment_in_advanced_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.generalMandate.joint_fulfillment_in_advanced_biofuel}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.joint_fulfillment_in_conventional_biofuel)}}>Joint fulfillment IN conventional biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.joint_fulfillment_in_conventional_biofuel)}}>{formatDisplayValue(data.generalMandate.joint_fulfillment_in_conventional_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.generalMandate.joint_fulfillment_in_conventional_biofuel}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.allocated_in_advanced_sub_mandate)}}>Allocated in advanced sub-mandate</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.allocated_in_advanced_sub_mandate)}}>{formatDisplayValue(data.generalMandate.allocated_in_advanced_sub_mandate)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.generalMandate.allocated_in_advanced_sub_mandate}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.allocation_advanced_biofuel_in_gasoline)}}>Allocation advanced biofuel in gasoline</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.allocation_advanced_biofuel_in_gasoline)}}>{formatDisplayValue(data.generalMandate.allocation_advanced_biofuel_in_gasoline)}</TableCell>
                                <TableCell style={{width: "30%"}}>{(Number(data.generalMandate.allocation_advanced_biofuel_in_gasoline) || 0) * 2}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.allocation_advanced_biofuel_in_diesel)}}>Allocation advanced biofuel in diesel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.allocation_advanced_biofuel_in_diesel)}}>{formatDisplayValue(data.generalMandate.allocation_advanced_biofuel_in_diesel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{(Number(data.generalMandate.allocation_advanced_biofuel_in_diesel) || 0) * 2}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.allocation_conventional_biofuel_in_gasoline)}}>Allocation conventional biofuel in gasoline</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.allocation_conventional_biofuel_in_gasoline)}}>{formatDisplayValue(data.generalMandate.allocation_conventional_biofuel_in_gasoline)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.generalMandate.allocation_conventional_biofuel_in_gasoline}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.generalMandate.allocation_conventional_biofuel_in_diesel)}}>Allocation conventional biofuel in diesel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.generalMandate.allocation_conventional_biofuel_in_diesel)}}>{formatDisplayValue(data.generalMandate.allocation_conventional_biofuel_in_diesel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.generalMandate.allocation_conventional_biofuel_in_diesel}</TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Remaining volume general mandate</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.generalMandate.remaining_volume_general_mandate)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Mandate surplus LOCKED</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell>Total volume surplus LOCKED</TableCell>
                                <TableCell>{formatDisplayValue(data.mandateSurplusLocked.total_volume_surplus_locked)}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.advanced_biofuel_in_gasoline_required_by_customers)}}>Advanced biofuel in gasoline required by customers</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.advanced_biofuel_in_gasoline_required_by_customers)}}>{formatDisplayValue(data.mandateSurplusLocked.advanced_biofuel_in_gasoline_required_by_customers)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.mandateSurplusLocked.advanced_biofuel_in_gasoline_required_by_customers == "N/A" ? "Not applicable" : ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.advanced_biofuel_in_diesel_required_by_customers)}}>Advanced biofuel in diesel required by customers</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.advanced_biofuel_in_diesel_required_by_customers)}}>{formatDisplayValue(data.mandateSurplusLocked.advanced_biofuel_in_diesel_required_by_customers)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.mandateSurplusLocked.advanced_biofuel_in_diesel_required_by_customers == "N/A" ? "Not applicable" : ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.conventional_biofuel_in_gasoline_required_by_customers)}}>Conventional biofuel in gasoline required by customers</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.conventional_biofuel_in_gasoline_required_by_customers)}}>{formatDisplayValue(data.mandateSurplusLocked.conventional_biofuel_in_gasoline_required_by_customers)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.mandateSurplusLocked.conventional_biofuel_in_gasoline_required_by_customers == "N/A" ? "Not applicable" : ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.conventional_biofuel_in_diesel_required_by_customers)}}>Conventional biofuel in diesel required by customers</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.conventional_biofuel_in_diesel_required_by_customers)}}>{formatDisplayValue(data.mandateSurplusLocked.conventional_biofuel_in_diesel_required_by_customers)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.mandateSurplusLocked.conventional_biofuel_in_diesel_required_by_customers == "N/A" ? "Not applicable" : ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel)}}>Joint fulfillment OUT advanced biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel)}}>{formatDisplayValue(data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}>{data.mandateSurplusLocked.joint_fulfillment_out_advanced_biofuel == "N/A" ? "Not applicable" : ""}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusLocked.joint_fulfillment_out_conventional_biofuel)}}>Joint fulfillment OUT conventional biofuel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusLocked.joint_fulfillment_out_conventional_biofuel)}}>{formatDisplayValue(data.mandateSurplusLocked.joint_fulfillment_out_conventional_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Remaining volume mandate surplus LOCKED</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.mandateSurplusLocked.remaining_volume_mandate_surplus_locked)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Mandate surplus AVAILABLE</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell>Total volume surplus AVAILABLE</TableCell>
                                <TableCell>{formatDisplayValue(data.mandateSurplusAvailable.total_volume_surplus_available)}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusAvailable.advanced_biofuel_in_gasoline)}}>Advanced biofuel in gasoline</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusAvailable.advanced_biofuel_in_gasoline)}}>{formatDisplayValue(data.mandateSurplusAvailable.advanced_biofuel_in_gasoline)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusAvailable.advanced_biofuel_in_diesel)}}>Advanced biofuel in diesel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusAvailable.advanced_biofuel_in_diesel)}}>{formatDisplayValue(data.mandateSurplusAvailable.advanced_biofuel_in_diesel)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusAvailable.conventional_biofuel_in_gasoline)}}>Conventional biofuel in gasoline</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusAvailable.conventional_biofuel_in_gasoline)}}>{formatDisplayValue(data.mandateSurplusAvailable.conventional_biofuel_in_gasoline)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%", color: getCellColor(data.mandateSurplusAvailable.conventional_biofuel_in_diesel)}}>Conventional biofuel in diesel</TableCell>
                                <TableCell style={{width: "30%", color: getCellColor(data.mandateSurplusAvailable.conventional_biofuel_in_diesel)}}>{formatDisplayValue(data.mandateSurplusAvailable.conventional_biofuel_in_diesel)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Remaining volume mandate surplus AVAILABLE</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.mandateSurplusAvailable.remaining_volume_mandate_surplus_available)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>

            <div style={{backgroundColor: "#fff", padding: "3px 18px", marginTop: "15px"}}>
                <h3>Volume Check</h3>
                <Card className={classes.card}>
                    <Table className={classes.table}>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell>Volume check</TableCell>
                                <TableCell>{data.volumeCheck.total_volumes == 0 ? "VOLUME MATCH" : "VOLUME ERROR"}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Joint fulfillment IN</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.volumeCheck.joint_fulfillment_in)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Joint fulfillment OUT</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.volumeCheck.joint_fulfillment_out)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Supplied biofuel</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.volumeCheck.supplied_biofuel)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableBody>
                        <TableHead className="MuiTableCell-head">
                            <TableRow>
                                <TableCell style={{width: "40%"}}>Total volumes</TableCell>
                                <TableCell style={{width: "30%"}}>{formatDisplayValue(data.volumeCheck.total_volumes)}</TableCell>
                                <TableCell style={{width: "30%"}}></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Card>
            </div>
        </div>
    );
};

export default PrelSurplusRoaduseCalculation;
